/**
 * Shared validation utilities for automated campaign stages
 */

// Event names that require options to be selected
const EVENTS_REQUIRING_OPTION = ['joinDate', 'orientationDate'];
const EVENT_CUSTOM = 'custom';

/**
 * Validates a stage name
 * @param {string} name - The stage name to validate
 * @returns {boolean} - True if valid
 */
export const validateStageName = name => {
  return typeof name === 'string' && name.trim().length > 0;
};

/**
 * Validates a stage template
 * @param {object} template - The template object to validate
 * @returns {boolean} - True if valid
 */
export const validateStageTemplate = template => {
  if (!template || typeof template !== 'object') {
    return false;
  }

  const hasName =
    template.name &&
    typeof template.name === 'string' &&
    template.name.trim().length > 0;
  const hasHtml =
    template.html &&
    typeof template.html === 'string' &&
    template.html.trim().length > 0;

  return hasName && hasHtml;
};

/**
 * Validates a condition's option field
 * @param {object} option - The option object to validate
 * @param {boolean} isRequired - Whether the option is required
 * @returns {boolean} - True if valid
 */
export const validateConditionOption = (option, isRequired) => {
  if (isRequired) {
    // Option must exist and have either names or anySelected
    if (!option) return false;
    return (
      (Array.isArray(option.names) && option.names.length > 0) ||
      option.anySelected === true
    );
  } else if (option) {
    // For other events, if option exists, it must be valid
    return (
      (Array.isArray(option.names) && option.names.length > 0) ||
      option.anySelected === true
    );
  }
  // Option is not required and doesn't exist - valid
  return true;
};

/**
 * Validates a single condition
 * @param {object} condition - The condition object to validate
 * @param {function} isDefined - Optional helper function to check if value is defined (defaults to !== undefined check)
 * @returns {boolean} - True if valid
 */
export const validateCondition = (
  condition,
  isDefined = val => val !== undefined
) => {
  // Return false if condition is undefined or null
  if (!condition || typeof condition !== 'object') {
    return false;
  }

  // Validate condition name
  const isNameValid =
    typeof condition.name === 'string' && condition.name.trim().length > 0;
  if (!isNameValid) return false;

  // Validate delay
  if (!condition.delay || typeof condition.delay !== 'object') return false;
  const isDelayValid =
    typeof condition.delay.value === 'number' &&
    isDefined(condition.delay.unit) &&
    isDefined(condition.delay.order);
  if (!isDelayValid) return false;

  // Validate option
  const requiresOption = EVENTS_REQUIRING_OPTION.includes(condition.name);
  const isOptionValid = validateConditionOption(
    condition.option,
    requiresOption
  );
  if (!isOptionValid) return false;

  // Validate custom date (required if event is 'custom')
  if (condition.name === EVENT_CUSTOM) {
    const isCustomDateValid =
      condition.date &&
      typeof condition.date === 'string' &&
      condition.date.trim().length > 0;
    if (!isCustomDateValid) return false;
  }

  return true;
};

/**
 * Validates a complete stage (name, template, and condition)
 * @param {object} stage - The stage object to validate
 * @param {function} isDefined - Optional helper function to check if value is defined (defaults to !== undefined check)
 * @returns {boolean} - True if valid
 */
export const validateStage = (stage, isDefined = val => val !== undefined) => {
  if (!stage) return false;

  // Validate name
  if (!validateStageName(stage.name)) return false;

  // Validate template
  if (!validateStageTemplate(stage.template)) return false;

  // Validate condition
  const condition = stage.conditions?.[0];
  if (!validateCondition(condition, isDefined)) return false;

  return true;
};

/**
 * Validates all stages in an array
 * @param {Array} stages - Array of stage objects to validate
 * @param {function} isDefined - Optional helper function to check if value is defined (defaults to !== undefined check)
 * @returns {boolean} - True if all stages are valid
 */
export const validateAllStages = (
  stages,
  isDefined = val => val !== undefined
) => {
  if (!stages || stages.length === 0) return false;
  return stages.every(stage => validateStage(stage, isDefined));
};
